import { v4 as uuidv4 } from 'uuid';
import { redactParsedUrl, redactUrlParams } from '@/shared/lib/redact-url';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

export type ClientEventArea =
  | 'api'
  | 'session'
  | 'scanner'
  | 'payment'
  | 'printing'
  | 'diagnostics'
  | 'system';

export type ClientEventLevel = 'debug' | 'info' | 'warning' | 'error';

export interface ClientEvent {
  event_id: string;
  timestamp: string;
  type: string;
  area: ClientEventArea;
  level: ClientEventLevel;
  frontend_trace_id: string;
  session_id: string | null;
  kiosk_slug: string | null;
  device_uuid: string | null;
  build_version: string | null;
  route: string | null;
  step: string | null;
  payload: unknown;
}

export interface ClientEventRecordOptions {
  area?: ClientEventArea;
  level?: ClientEventLevel;
}

export interface ClientEventSnapshot {
  context: ReturnType<typeof readContext>;
  state: ReturnType<typeof readState>;
  events: ClientEvent[];
}

const STORAGE_KEY = 'kiosk_client_events_v1';
const TRACE_STORAGE_KEY = 'kiosk_frontend_trace_id';
const MAX_EVENTS = 500;
const DEFAULT_AREA: ClientEventArea = 'system';
const DEFAULT_LEVEL: ClientEventLevel = 'info';
const MAX_STRING_LENGTH = 500;
const MAX_DEPTH = 6;

// Do not add kiosk API keys, ticket/download/GoPhoto codes, QR payloads,
// session IDs, visitor IDs, or device IDs here. Those are operational join
// keys we intentionally send to Sentry so kiosk, Postgres, Stripe, and visitor
// support traces can be correlated without guesswork.
const SENSITIVE_KEY_PATTERNS = [
  /authorization/i,
  /bearer/i,
  /token/i,
  /secret/i,
  /password/i,
  /signature/i,
  /^sig$/i,
  /^se$/i,
  /^sv$/i,
  /^sr$/i,
  /^sp$/i,
  /card/i,
  /pan/i,
  /cvv/i,
  /cvc/i,
  /expiry/i,
];

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage may be unavailable or full on locked-down kiosk browsers.
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
}

function readEventsFromStorage(): ClientEvent[] {
  const raw = storageGet(STORAGE_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-MAX_EVENTS) : [];
  } catch {
    return [];
  }
}

let events: ClientEvent[] = readEventsFromStorage();

function readTraceId(): string {
  const existing = storageGet(TRACE_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const created = uuidv4();
  storageSet(TRACE_STORAGE_KEY, created);
  return created;
}

let frontendTraceId = readTraceId();
let attachedSessionId: string | null = null;

function persistEvents(): void {
  storageSet(STORAGE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

function sanitizeUrlString(value: string): string {
  if (/^blob:/i.test(value)) {
    return '[blob URL]';
  }

  const trimmed = value.trim();
  const looksLikeUrl = /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/');
  if (!looksLikeUrl) {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
      : value;
  }

  try {
    const url = new URL(trimmed, window.location.origin);
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return redactParsedUrl(url, trimmed.startsWith('/'));
    }
  } catch {
    const sanitized = redactUrlParams(value);
    return sanitized.length > MAX_STRING_LENGTH
      ? `${sanitized.slice(0, MAX_STRING_LENGTH)}...[truncated]`
      : sanitized;
  }
  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
    : value;
}

export function sanitizePayload(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === 'string') {
    return sanitizeUrlString(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof File !== 'undefined' && value instanceof File) {
    return { file: true, name: value.name, type: value.type, size: value.size };
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return { blob: true, type: value.type || null, size: value.size };
  }
  if (typeof value !== 'object') {
    return String(value);
  }
  if (seen.has(value)) {
    return '[Circular]';
  }
  if (depth >= MAX_DEPTH) {
    return '[MaxDepth]';
  }

  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .slice(0, 50)
      .map((item) => sanitizePayload(item, depth + 1, seen));
  }

  const source = value as Record<string, unknown>;
  const sanitized: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    if (isSensitiveKey(key)) {
      sanitized[key] = '[REDACTED]';
      continue;
    }
    sanitized[key] = sanitizePayload(source[key], depth + 1, seen);
  }
  return sanitized;
}

function readWindowObject<T extends Record<string, unknown>>(
  key: string
): T | null {
  const value = (window as unknown as Record<string, unknown>)[key];
  return value && typeof value === 'object' ? (value as T) : null;
}

function readContext() {
  const store = readWindowObject('__store');
  const router = readWindowObject('__Router');
  const hardware = readWindowObject('__KioskHardware');
  const deploymentVersion = readWindowObject('__DeploymentVersion');
  const kiosk = store?.kiosk as Record<string, unknown> | null | undefined;
  const device = store?.device as Record<string, unknown> | null | undefined;

  return {
    frontend_trace_id: frontendTraceId,
    session_id:
      (store?.sessionId as string | null | undefined) ?? attachedSessionId,
    kiosk_slug: (kiosk?.slug as string | null | undefined) ?? null,
    kiosk_mode: (store?.kioskMode as string | null | undefined) ?? null,
    is_test: kiosk?.for_test === true,
    device_uuid:
      (device?.device_uuid as string | null | undefined) ??
      ((hardware?.device as Record<string, unknown> | undefined)?.device_uuid as
        | string
        | null
        | undefined) ??
      null,
    build_version:
      (deploymentVersion?._currentVersion as string | null | undefined) ?? null,
    route:
      ((router?.route as Record<string, unknown> | undefined)?.path as
        | string
        | null
        | undefined) ?? window.location.pathname,
    step: (router?.step as string | null | undefined) ?? null,
  };
}

function readState() {
  const payment = readWindowObject('__Payment');
  const printing = readWindowObject('__Printing');
  const store = readWindowObject('__store');
  const router = readWindowObject('__Router');

  return sanitizePayload({
    network: {
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      connection: (
        navigator as Navigator & {
          connection?: Record<string, unknown>;
        }
      ).connection
        ? {
            effectiveType: (
              navigator as Navigator & {
                connection?: Record<string, unknown>;
              }
            ).connection?.effectiveType,
            downlink: (
              navigator as Navigator & {
                connection?: Record<string, unknown>;
              }
            ).connection?.downlink,
            rtt: (
              navigator as Navigator & {
                connection?: Record<string, unknown>;
              }
            ).connection?.rtt,
          }
        : null,
    },
    route: {
      path: (router?.route as Record<string, unknown> | undefined)?.path,
      step: router?.step,
    },
    session: {
      sessionId: store?.sessionId,
      isResettingSession: store?.isResettingSession,
      gophotoCodeSet: Boolean(store?.gophotoCode),
    },
    payment: payment
      ? {
          isStripePayment: payment.isStripePayment,
          isPaymentInProgress: payment.isPaymentInProgress,
          paymentDestination: payment.paymentDestination,
          isAborted: payment.isAborted,
          paymentIntentSentToReader: payment._paymentIntentSentToReader,
          hasLastCaptureReadyIntent: Boolean(payment._lastCaptureReadyIntentId),
        }
      : null,
    printing: printing
      ? {
          queueLength: Array.isArray(printing.queue)
            ? printing.queue.length
            : 0,
          totalCount: printing.totalCount,
          hasErrors: printing.hasErrors,
          isPrintingInProgress: printing.isPrintingInProgress,
          shouldPrintViaBrowser: printing.shouldPrintViaBrowser,
          printerVertical: printing.printerVertical,
          printerHorizontal: printing.printerHorizontal,
        }
      : null,
  });
}

function writeSentryContext(): void {
  const sentry = readWindowObject<{
    setTag?: (key: string, value: string) => void;
    setContext?: (key: string, value: unknown) => void;
  }>('__SentryService');
  try {
    sentry?.setTag?.('frontend_trace_id', frontendTraceId);
    sentry?.setContext?.('frontend_trace', {
      frontend_trace_id: frontendTraceId,
    });
  } catch {
    // Sentry must never break diagnostics.
  }
}

export const clientEvents = {
  startTrace(reason = 'manual'): string {
    frontendTraceId = uuidv4();
    attachedSessionId = null;
    storageSet(TRACE_STORAGE_KEY, frontendTraceId);
    writeSentryContext();
    this.record('trace_started', { reason }, { area: 'system' });
    return frontendTraceId;
  },

  attachSession(sessionId: string | null | undefined): void {
    attachedSessionId = sessionId || null;
    if (attachedSessionId) {
      this.record(
        'trace_session_attached',
        { session_id: attachedSessionId },
        { area: 'session' }
      );
    }
  },

  endTrace(outcome = 'unknown'): void {
    this.record('trace_ended', { outcome }, { area: 'system' });
    attachedSessionId = null;
  },

  record(
    type: string,
    payload: unknown = {},
    options: ClientEventRecordOptions = {}
  ): ClientEvent {
    const context = readContext();
    const event: ClientEvent = {
      event_id: uuidv4(),
      timestamp: new Date().toISOString(),
      type,
      area: options.area ?? DEFAULT_AREA,
      level: options.level ?? DEFAULT_LEVEL,
      frontend_trace_id: frontendTraceId,
      session_id: context.session_id ?? null,
      kiosk_slug: context.kiosk_slug ?? null,
      device_uuid: context.device_uuid ?? null,
      build_version: context.build_version ?? null,
      route: context.route ?? null,
      step: context.step ?? null,
      payload: sanitizePayload(payload),
    };

    events = [...events, event].slice(-MAX_EVENTS);
    persistEvents();
    return event;
  },

  getTraceId(): string {
    return frontendTraceId;
  },

  getContext(): ReturnType<typeof readContext> {
    return readContext();
  },

  getEvents(limit?: number): ClientEvent[] {
    return limit ? events.slice(-limit) : [...events];
  },

  snapshot(limit = 100): ClientEventSnapshot {
    return {
      context: readContext(),
      state: readState(),
      events: this.getEvents(limit),
    };
  },

  clear(): void {
    events = [];
    storageRemove(STORAGE_KEY);
    this.record('timeline_cleared', {}, { area: 'diagnostics' });
  },
};

writeSentryContext();
exposeToGlobalScope(clientEvents, 'ClientEvents');
